import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  linkPlugin,
  linkDialogPlugin,
  imagePlugin,
  tablePlugin,
  codeBlockPlugin,
  codeMirrorPlugin,
  diffSourcePlugin,
  toolbarPlugin,
  DiffSourceToggleWrapper,
  UndoRedo,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  ListsToggle,
  CreateLink,
  InsertImage,
  InsertTable,
  InsertThematicBreak,
  Separator
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import './RichMarkdownEditor.css';
import { customSwal } from '../CustomSwal';
import { uploadConsoleImage } from './consoleMediaApi';

// Word-processor style editor that still saves markdown, so the public API and customer sites don't change.
// Takes the same value/onChange/uploadTarget props as MarkdownEditor so the two can be swapped.
const RichMarkdownEditor = ({ value, onChange, minHeight = 360, uploadTarget }) => {
  const token = useSelector((state) => state.userLogin.userInfo?.token);
  const editorRef = useRef(null);
  const lastValueRef = useRef(value || '');
  const [parseError, setParseError] = useState(null);

  // Plugins are only read on mount, so the upload handler reads the latest target and token through a ref
  const uploadRef = useRef({ uploadTarget, token });
  uploadRef.current = { uploadTarget, token };
  const canUpload = Boolean(uploadTarget?.consoleId || uploadTarget?.consoleItemId);

  // `markdown` is only read on mount; push in values the parent sets later (e.g. a modal loading an item)
  useEffect(() => {
    const next = value || '';
    if (next !== lastValueRef.current) {
      lastValueRef.current = next;
      editorRef.current?.setMarkdown(next);
    }
  }, [value]);

  const plugins = useMemo(() => [
    headingsPlugin(),
    listsPlugin(),
    quotePlugin(),
    thematicBreakPlugin(),
    linkPlugin(),
    linkDialogPlugin(),
    tablePlugin(),
    imagePlugin({
      imageUploadHandler: async (file) => {
        const { uploadTarget: target, token: authToken } = uploadRef.current;
        try {
          const { url } = await uploadConsoleImage(file, target, authToken);
          return url;
        } catch (error) {
          customSwal.fire({ icon: 'error', title: 'Image upload failed', text: error.message });
          throw error;
        }
      },
    }),
    codeBlockPlugin({ defaultCodeBlockLanguage: '' }),
    codeMirrorPlugin({
      codeBlockLanguages: { '': 'Plain text', js: 'JavaScript', css: 'CSS', html: 'HTML' },
      autoLoadLanguageSupport: false,
    }),
    diffSourcePlugin({ viewMode: 'rich-text' }),
    toolbarPlugin({
      toolbarContents: () => (
        <DiffSourceToggleWrapper options={['rich-text', 'source']}>
          <UndoRedo />
          <Separator />
          {/* No underline: markdown has none, it would be saved as raw HTML */}
          <BoldItalicUnderlineToggles options={['Bold', 'Italic']} />
          <Separator />
          <BlockTypeSelect />
          <Separator />
          <ListsToggle options={['bullet', 'number']} />
          <Separator />
          <CreateLink />
          <InsertImage />
          <InsertTable />
          <InsertThematicBreak />
        </DiffSourceToggleWrapper>
      ),
    }),
    markdownShortcutPlugin(),
  ], []);

  const handleChange = (markdown, initialMarkdownNormalize) => {
    // Loading content can reformat it slightly (spacing, list markers); don't count that as an edit
    if (initialMarkdownNormalize) return;
    lastValueRef.current = markdown;
    onChange(markdown);
  };

  return (
    <div className="text-left" style={{ '--console-editor-min-height': `${minHeight}px` }}>
      {parseError && (
        <div className="mb-2 rounded border border-yellow-600/60 bg-yellow-900/30 p-2 text-xs text-yellow-100">
          Some of this content can't be shown in the visual editor ({parseError}). Use the Markdown view
          (toggle at the right of the toolbar) to edit it.
        </div>
      )}
      <div className="overflow-hidden rounded-md border border-gray-600 bg-gray-900">
        <MDXEditor
          ref={editorRef}
          markdown={value || ''}
          onChange={handleChange}
          onError={({ error }) => setParseError(error)}
          plugins={plugins}
          className="dark-theme console-rich-editor"
          contentEditableClassName="prose prose-invert max-w-none px-4 py-3"
          placeholder="Start writing…"
        />
      </div>
      {canUpload && (
        <p className="mt-1 text-xs text-gray-400">
          Tip: paste or drag images straight into the editor, or use the image button in the toolbar.
        </p>
      )}
    </div>
  );
};

export default RichMarkdownEditor;
